import { defineStore } from "pinia";
import { store } from "/@/store";
import { router } from "/@/router";
import { h } from "vue";
import {
  LoginParams,
  ResetPasswordParams,
  SignupParams,
  UserInfo,
} from "@/models/user";
import { PageEnum } from "@/enums/pageEnum";
import { Modal } from "ant-design-vue";
import axios from "@/http/axios";
import { useAppStoreWithOut } from "./app";

const LOGIN_TOKEN = () => `LOGIN_TOKEN`;

export const useUserStore = defineStore("app-user", {
  state: () => ({
    userInfo: null as Nullable<UserInfo>,
    token: undefined as string | undefined,
  }),
  getters: {
    getUserInfo(): UserInfo | null {
      return this.userInfo;
    },
    getToken(): string {
      return this.token || localStorage.getItem(LOGIN_TOKEN()) || "";
    },
    getUserDesc(): string {
      return this.userInfo?.email || "";
    },
    getHomePage(): string {
      return PageEnum.HOME_PAGE;
    },
  },
  actions: {
    setToken(token: string | null) {
      this.token = token ?? "";
      localStorage.setItem(LOGIN_TOKEN(), token ?? "");
    },
    setUserInfo(info: UserInfo | null) {
      this.userInfo = info ?? null;
    },
    resetState() {
      this.setToken(null);
      this.setUserInfo(null);
    },
    async login(
      params: LoginParams & {
        redirect?: string;
        origin?: string;
      },
    ): Promise<UserInfo | null> {
      const data = await axios.get<{ token: string }>({
        url: "auth/:account",
        params: { account: params.email },
        query: {
          password: params["password"] || "empty",
          captcha: params["captcha"],
        },
      });
      if (!data) return null;

      this.setToken(data.token);
      const userInfo = await this.afterLoginAction();
      if (userInfo) {
        await this.goHome(params.redirect, params.origin);
      }
      return userInfo;
    },
    async signup(
      params: SignupParams & {
        redirect?: string;
        origin?: string;
      },
    ): Promise<UserInfo | null> {
      const data = await axios.post<{ token: string }>({
        url: "auth",
        data: {
          email: params.email,
          password: params.password,
          captcha: params.captcha,
        },
      });
      if (!data) return null;

      this.setToken(data.token);
      const userInfo = await this.afterLoginAction();
      if (userInfo) {
        await this.goHome(params.redirect, params.origin);
      }
      return userInfo;
    },
    async resetPassword(
      params: ResetPasswordParams & {
        redirect?: string;
        origin?: string;
      },
    ): Promise<UserInfo | null> {
      const data = await axios.patch<{ token: string }>({
        url: "auth/password",
        data: {
          email: params.email,
          password: params.password,
          captcha: params.captcha,
        },
      });
      if (!data) return null;

      this.setToken(data.token);
      const userInfo = await this.afterLoginAction();
      if (userInfo) {
        await this.goHome(params.redirect, params.origin);
      }
      return userInfo;
    },
    async goHome(redirect?: string, origin?: string): Promise<void> {
      if (origin) {
        location.href = `${origin}#/?redirect=${encodeURIComponent(redirect as string)}&token=${encodeURIComponent(this.getToken)}`;
        return;
      }
      if (redirect == PageEnum.BASE_LOGIN) {
        redirect = undefined;
      }
      if (!redirect) {
        redirect =
          (router.currentRoute.value.query.redirect as string) || undefined;
      }

      await router.replace(redirect ?? this.getHomePage);
    },
    async afterLoginAction(goHome?: boolean): Promise<UserInfo | null> {
      if (!this.getToken) return null;
      const userInfo = await this.getUserInfoAction();
      if (!userInfo) return null;

      goHome && (await router.replace(this.getHomePage));

      return userInfo;
    },
    async getUserInfoAction(): Promise<UserInfo | null> {
      if (!this.getToken) return null;
      const userInfo = await axios.get<UserInfo>({
        url: "user",
      });
      if (!userInfo) return null;

      useAppStoreWithOut().setServerTime(userInfo.serverTime);
      this.setUserInfo(userInfo);
      return userInfo;
    },
    async logout(goLogin = false, useApi = false): Promise<boolean> {
      if (useApi && !!this.getToken) {
        await axios.delete({
          url: "auth",
        });
      }
      this.resetState();

      if (goLogin) {
        await router.push({
          path: PageEnum.BASE_LOGIN,
        });
      }
      return true;
    },
    confirmLoginOut() {
      Modal.confirm({
        iconType: "warning",
        title: () => h("span", "温馨提醒"),
        content: () => h("span", "是否确认退出系统?"),
        onOk: async () => {
          await this.logout(true);
        },
        onCancel: async () => {},
        okText: "确认退出",
        cancelText: "取消",
      });
    },
  },
});

// Need to be used outside the setup
export function useUserStoreWithOut() {
  return useUserStore(store);
}
